import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { useBasket } from '@/lib/cart';
import CourseCard      from '@/components/CourseCard';
import CourseModal     from '@/components/CourseModal';
import IntroAnimation  from '@/components/IntroAnimation';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../../server/src/routers/index';

type Course = inferRouterOutputs<AppRouter>['courses']['list'][number];

// ─── Helpers ─────────────────────────────────────────────────────────────────
/** "2026-06-20" → "Sat 20 Jun 2026" */
function formatDateLabel(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(`${iso}T00:00:00`) : iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** "10:00:00" → "10:00" */
function formatTimeLabel(t: string): string {
  return t.slice(0, 5);
}

/** Normalise a session date (string or Date) to "YYYY-MM-DD" in local time. */
function toIsoDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Build a YYYY-MM-DD key for an arbitrary year/month/day triple. */
function dateKey(year: number, monthIdx: number, day: number): string {
  return `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const DAY_NAMES   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March',     'April',   'May',      'June',
  'July',    'August',   'September', 'October', 'November', 'December',
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const { user, signIn } = useAuth();
  const { addItem } = useBasket();
  const nav = useNavigate();

  // ── Live data ─────────────────────────────────────────────────────────────
  const { data: courses = [], isLoading: loadingCourses } =
    trpc.courses.list.useQuery();
  const { data: sessions = [], isLoading: loadingSessions } =
    trpc.sessions.list.useQuery();
  const { data: instructors = [] } = trpc.instructors.list.useQuery();
  const { data: categories = [] }  = trpc.categories.list.useQuery();
  const { data: myPurchases = [] } = trpc.purchases.getUserPurchases.useQuery(
    undefined, { enabled: !!user }
  );

  const purchasedIds = new Set(myPurchases.map(p => p.courseId));

  const initiatePayment = trpc.purchases.initiatePayment.useMutation({
    onSuccess: (res) => { window.location.href = res.paymentUrl; },
  });

  const createBooking = trpc.bookings.create.useMutation({
    onSuccess: () => {
      setBookingDone(true);
      setBookingModal(false);
      setBookingForm({ topic: '', message: '', mobileNumber: '' });
      setActiveSessionId(null);
    },
  });

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeCourse,    setActiveCourse]    = useState<Course | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [bookingModal,    setBookingModal]    = useState(false);
  const [bookingDone,     setBookingDone]     = useState(false);
  const [bookingForm,     setBookingForm]     = useState({
    topic: '', message: '', mobileNumber: '',
  });

  // Calendar state — first of the month being displayed
  const [monthCursor, setMonthCursor] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Homepage browsing state ('other' = courses with no major)
  const [selectedCategoryId,   setSelectedCategoryId]   = useState<number | 'other' | null>(null);
  const [selectedInstructorId, setSelectedInstructorId] = useState<number | null>(null);

  const activeSession = useMemo(
    () => sessions.find(s => s.id === activeSessionId) ?? null,
    [sessions, activeSessionId],
  );

  // Featured (popular) courses for the top row.
  const featuredCourses = useMemo(
    () => courses.filter(c => (c as any).isFeatured),
    [courses],
  );

  // A course can belong to several majors (join relation); fall back to the
  // legacy single categoryId for older data.
  const majorIdsOf = (c: any): number[] => {
    const fromJoin = (c.courseCategories ?? []).map((cc: any) => cc.categoryId);
    if (fromJoin.length) return fromJoin;
    return c.categoryId != null ? [c.categoryId] : [];
  };

  // Courses in the selected major ('other' = uncategorised).
  const categoryCourses = useMemo(
    () => selectedCategoryId == null
      ? []
      : selectedCategoryId === 'other'
        ? courses.filter(c => majorIdsOf(c).length === 0)
        : courses.filter(c => majorIdsOf(c).includes(selectedCategoryId)),
    [courses, selectedCategoryId],
  );
  const hasOtherCourses = useMemo(
    () => courses.some(c => majorIdsOf(c).length === 0),
    [courses],
  );

  // Sessions shown in the calendar: all, or just the chosen instructor's.
  const visibleSessions = useMemo(
    () => selectedInstructorId == null
      ? sessions
      : sessions.filter(s => (s as any).instructorId === selectedInstructorId),
    [sessions, selectedInstructorId],
  );

  // ── Calendar derivations ───────────────────────────────────────────────────
  const monthYear = monthCursor.getFullYear();
  const monthIdx  = monthCursor.getMonth();
  const daysInThisMonth = new Date(monthYear, monthIdx + 1, 0).getDate();
  const dowOfFirst      = new Date(monthYear, monthIdx, 1).getDay();
  const todayIso        = toIsoDate(new Date());

  /**
   * Group sessions by their date key, separating open vs full counts so we
   * can decide both whether to show a dot and whether the date is clickable.
   */
  const sessionsByDate = useMemo(() => {
    const map = new Map<string, { open: number; full: number; all: typeof sessions }>();
    for (const s of visibleSessions) {
      const key = toIsoDate(s.date);
      if (!key) continue;
      const bucket = map.get(key) ?? { open: 0, full: 0, all: [] as typeof sessions };
      bucket.all.push(s);
      if (s.availableSlots > 0) bucket.open += 1;
      else                      bucket.full += 1;
      map.set(key, bucket);
    }
    return map;
  }, [visibleSessions]);

  const sessionsForSelectedDate = selectedDate
    ? (sessionsByDate.get(selectedDate)?.all ?? [])
        .slice()
        .sort((a, b) => a.time.localeCompare(b.time))
    : [];

  const goPrevMonth = () => setMonthCursor(new Date(monthYear, monthIdx - 1, 1));
  const goNextMonth = () => setMonthCursor(new Date(monthYear, monthIdx + 1, 1));

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handlePurchase = (id: number) => {
    if (!user) { signIn('/'); return; }
    const course = courses.find(c => c.id === id);
    if (course) {
      addItem({ id: course.id, title: course.title, price: parseFloat(course.price as any) });
    }
    initiatePayment.mutate({ courseIds: [id] });
  };

  const handleSessionClick = (id: number) => {
    if (!user) { signIn('/'); return; }
    const target = sessions.find(s => s.id === id);
    if (!target || target.availableSlots <= 0) return; // booked / full — no-op
    setActiveSessionId(id);
    setBookingModal(true);
  };

  const handleDateClick = (iso: string) => {
    setSelectedDate(iso);
    setActiveSessionId(null);
  };

  const submitBooking = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSession || !bookingForm.mobileNumber) return;
    if (bookingForm.message.trim().length < 10) {
      alert('Please write a message of at least 10 characters describing what you want to cover.');
      return;
    }
    createBooking.mutate({
      sessionId:    activeSession.id,
      topic:        bookingForm.topic,
      message:      bookingForm.message,
      mobileNumber: bookingForm.mobileNumber,
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const inputCls =
    'w-full bg-[#18181B] border border-white/10 rounded-2xl text-white px-4 py-3 ' +
    'text-sm outline-none focus:border-yellow-400/50 placeholder-zinc-600 transition-colors';

  return (
    <div className="bg-black min-h-screen text-[#E4E4E7]">

      {/* Logo intro — fades in big, then flies to the navbar logo spot. */}
      <IntroAnimation />

      {/* ── POPULAR (featured) courses ──────────────────────────────────── */}
      <section id="courses" className="bg-black pt-10 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black text-white mb-8 tracking-tight">Popular Courses</h2>

          {loadingCourses ? (
            <div className="flex justify-center py-16">
              <div className="w-7 h-7 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : featuredCourses.length === 0 ? (
            <div className="text-center py-12 text-[#52525B]">
              <p className="text-4xl mb-3">⭐</p>
              <p>No featured courses yet.</p>
            </div>
          ) : (
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {featuredCourses.map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  purchased={purchasedIds.has(course.id)}
                  onPurchase={handlePurchase}
                  onClick={setActiveCourse}
                />
              ))}
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={() => nav('/courses')}
              className="text-sm font-semibold text-yellow-400 hover:text-yellow-300"
            >
              Browse all courses →
            </button>
          </div>
        </div>
      </section>

      {/* ── MAJORS — pick one to see its courses ────────────────────────── */}
      <section id="majors" className="bg-black pt-12 pb-14 px-6 border-t border-white/[0.04]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-black text-white mb-4 tracking-tight">Majors</h2>

          {categories.length === 0 && !hasOtherCourses ? (
            <p className="text-[#52525B] text-sm">No majors yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map(cat => {
                const active = selectedCategoryId === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategoryId(active ? null : cat.id)}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all border
                                ${active
                                  ? 'bg-yellow-400 text-black border-yellow-400'
                                  : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
                  >
                    {cat.name}
                  </button>
                );
              })}
              {hasOtherCourses && (
                <button
                  onClick={() => setSelectedCategoryId(selectedCategoryId === 'other' ? null : 'other')}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all border
                              ${selectedCategoryId === 'other'
                                ? 'bg-yellow-400 text-black border-yellow-400'
                                : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
                >
                  Other
                </button>
              )}
            </div>
          )}

          {selectedCategoryId == null ? (
            categories.length > 0 && (
              <p className="text-[#52525B] text-sm">Pick a major to see its courses.</p>
            )
          ) : categoryCourses.length === 0 ? (
            <p className="text-[#52525B] text-sm">No courses in this major yet.</p>
          ) : (
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {categoryCourses.map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  purchased={purchasedIds.has(course.id)}
                  onPurchase={handlePurchase}
                  onClick={setActiveCourse}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Course modal (shared component) ─────────────────────────────── */}
      {activeCourse && (
        <CourseModal
          course={activeCourse}
          purchased={purchasedIds.has(activeCourse.id)}
          onPurchase={handlePurchase}
          onClose={() => setActiveCourse(null)}
        />
      )}

      {/* ── ONLINE SESSIONS — calendar + time-slot grid ─────────────────── */}
      <section id="booking" className="bg-black py-20 px-6 border-t border-white/[0.04]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black text-white mb-2 tracking-tight">Book Online Session</h2>
          <p className="text-[#71717A] text-sm mb-8">
            Pick an instructor, then a date and time — your booking is reviewed and confirmed via WhatsApp.
          </p>

          {/* Instructor picker — tapping one filters the calendar to their sessions */}
          {instructors.length > 0 && (
            <div className="mb-10">
              <h3 className="text-sm font-bold text-[#A1A1AA] uppercase tracking-widest mb-4">
                Choose an instructor
              </h3>
              <div className="flex flex-wrap gap-5">
                {instructors.map(ins => {
                  const active = selectedInstructorId === ins.id;
                  return (
                    <button
                      key={ins.id}
                      onClick={() => {
                        setSelectedInstructorId(active ? null : ins.id);
                        setSelectedDate(null);
                        setActiveSessionId(null);
                      }}
                      className="flex flex-col items-center gap-2 group w-20"
                    >
                      <div className={`w-20 h-20 rounded-3xl overflow-hidden bg-[#18181B] border-2 transition-all
                                      ${active
                                        ? 'border-yellow-400 shadow-[0_0_24px_rgba(234,179,8,0.4)] scale-105'
                                        : 'border-[#27272A] group-hover:border-yellow-400/40'}`}>
                        {(ins as any).iconUrl
                          ? <img src={(ins as any).iconUrl} alt={ins.name} className="w-full h-full object-contain" />
                          : <div className="w-full h-full flex items-center justify-center text-[#3F3F46] text-2xl">?</div>}
                      </div>
                      <span className={`text-xs font-semibold text-center leading-tight
                                       ${active ? 'text-yellow-400' : 'text-[#A1A1AA]'}`}>
                        {ins.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {selectedInstructorId != null && (
                <p className="text-[#52525B] text-xs mt-4">
                  Showing {instructors.find(i => i.id === selectedInstructorId)?.name}'s sessions ·
                  <button onClick={() => setSelectedInstructorId(null)}
                          className="text-yellow-400 hover:text-yellow-300 ml-1">show all</button>
                </p>
              )}
            </div>
          )}

          {/* Month header + prev/next */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#A1A1AA] uppercase tracking-widest">
              {MONTH_NAMES[monthIdx]} {monthYear}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={goPrevMonth}
                className="w-8 h-8 rounded-xl bg-[#18181B] border border-[#27272A]
                           text-[#A1A1AA] hover:text-white hover:border-yellow-400/40 transition-colors"
                aria-label="Previous month"
              >‹</button>
              <button
                onClick={goNextMonth}
                className="w-8 h-8 rounded-xl bg-[#18181B] border border-[#27272A]
                           text-[#A1A1AA] hover:text-white hover:border-yellow-400/40 transition-colors"
                aria-label="Next month"
              >›</button>
            </div>
          </div>

          {/* Day-of-week header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-[10px] font-bold text-[#52525B] pb-1">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 mb-10">
            {Array.from({ length: dowOfFirst }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}

            {Array.from({ length: daysInThisMonth }, (_, i) => i + 1).map(day => {
              const iso       = dateKey(monthYear, monthIdx, day);
              const bucket    = sessionsByDate.get(iso);
              const hasOpen   = (bucket?.open ?? 0) > 0;
              const isPast    = iso < todayIso;
              const isSelected = selectedDate === iso;
              const isClickable = hasOpen && !isPast;

              return (
                <button
                  key={day}
                  disabled={!isClickable}
                  onClick={() => isClickable && handleDateClick(iso)}
                  className={`relative aspect-square rounded-2xl flex flex-col items-center
                              justify-center transition-all text-sm font-bold
                              ${isPast
                                ? 'text-[#3F3F46] cursor-not-allowed'
                                : isSelected
                                  ? 'bg-yellow-400 text-black shadow-[0_0_20px_rgba(234,179,8,0.4)]'
                                  : isClickable
                                    ? 'bg-[#18181B] text-[#E4E4E7] hover:bg-[#27272A] hover:scale-105'
                                    : 'bg-[#0A0A0B] text-[#52525B] cursor-not-allowed'}`}
                >
                  {/* Availability dot */}
                  {hasOpen && !isPast && !isSelected && (
                    <span className="absolute top-1.5 left-1/2 -translate-x-1/2
                                     w-1.5 h-1.5 rounded-full bg-yellow-400" />
                  )}
                  <span>{day}</span>
                </button>
              );
            })}
          </div>

          {/* Selected-day time slots */}
          {loadingSessions ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedDate ? (
            <>
              <h3 className="text-sm font-bold text-[#A1A1AA] uppercase tracking-widest mb-4">
                Available Times — {formatDateLabel(selectedDate)}
              </h3>

              {sessionsForSelectedDate.length === 0 ? (
                <p className="text-[#52525B] text-sm mb-10">
                  No sessions scheduled on this date.
                </p>
              ) : (
                <div className="flex flex-wrap gap-4 mb-10">
                  {sessionsForSelectedDate.map(s => {
                    const isFull = s.availableSlots <= 0;
                    const isSelected = activeSessionId === s.id;
                    return (
                      <button
                        key={s.id}
                        disabled={isFull}
                        onClick={() => handleSessionClick(s.id)}
                        style={{ width: 140, height: 140 }}
                        className={`rounded-3xl flex flex-col items-center justify-center
                                    gap-1 transition-all font-bold relative
                                    ${isFull
                                      ? 'bg-[#0A0A0B] text-[#52525B] cursor-not-allowed border border-[#27272A]/60'
                                      : isSelected
                                        ? 'bg-yellow-400 text-black shadow-[0_0_24px_rgba(234,179,8,0.45)]'
                                        : 'bg-[#18181B] text-white hover:bg-[#27272A] hover:scale-105'}`}
                      >
                        <span className="text-lg leading-tight">{formatTimeLabel(s.time)}</span>
                        <span className={`text-[11px] font-bold
                                          ${isFull
                                            ? 'text-[#52525B]'
                                            : isSelected ? 'text-black' : 'text-yellow-400'}`}>
                          {parseFloat((s as any).price as string) === 0
                            ? 'Free'
                            : `${(s as any).price} KD`}
                        </span>
                        <span className={`text-[10px] mt-0.5 font-medium
                                          ${isFull
                                            ? 'text-[#52525B]'
                                            : isSelected ? 'text-black/60' : 'text-[#A1A1AA]'}`}>
                          {isFull
                            ? 'Fully booked'
                            : `${s.availableSlots}/${s.capacity} open`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="text-[#52525B] text-sm mb-10">
              Select a date with availability (•) to see open time slots.
            </p>
          )}

          {bookingDone && (
            <div className="mt-2 bg-[#18181B] rounded-3xl p-8 text-center border border-yellow-400/20">
              <div className="text-4xl mb-3">🗓️</div>
              <h3 className="text-white font-black text-xl mb-2">Request received</h3>
              <p className="text-[#A1A1AA] text-sm">
                Your booking is pending approval. You'll be contacted on the number you provided
                once the session is confirmed.
              </p>
              <button
                onClick={() => setBookingDone(false)}
                className="mt-5 px-6 py-2.5 bg-yellow-400 text-black font-bold
                           rounded-full text-sm hover:bg-yellow-300 transition-colors"
              >
                Book another
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── BOOKING MODAL ────────────────────────────────────────────────── */}
      {bookingModal && activeSession && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50
                     flex items-center justify-center p-5"
          onClick={e => e.target === e.currentTarget && setBookingModal(false)}
        >
          <div className="bg-[#09090B] border border-white/10 rounded-3xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start justify-between mb-1">
                <h3 className="text-xl font-black text-white">Session Details</h3>
                <button
                  onClick={() => setBookingModal(false)}
                  className="text-[#52525B] hover:text-white text-xl leading-none transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-yellow-400 text-sm font-medium mb-4">
                {formatDateLabel(activeSession.date)} · {formatTimeLabel(activeSession.time)}
              </p>

              {/* Price + availability */}
              <div className="flex items-center justify-between bg-[#18181B]
                              border border-[#27272A] rounded-2xl px-4 py-3 mb-6">
                <span className="text-[#A1A1AA] text-sm">Price</span>
                <span className="text-yellow-400 font-black text-lg">
                  {parseFloat((activeSession as any).price as string) === 0
                    ? 'Free'
                    : `${(activeSession as any).price} KD`}
                </span>
              </div>

              <div className="h-px bg-white/[0.06] mb-6" />
              <p className="text-[#71717A] text-xs mb-6">
                Booking is a request — admin will confirm and arrange payment via WhatsApp.
              </p>

              <form onSubmit={submitBooking} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-[#71717A] uppercase tracking-widest mb-2">
                    Topic
                  </label>
                  <select
                    value={
                      courses.some(c => c.title === bookingForm.topic)
                        ? bookingForm.topic
                        : bookingForm.topic === '' ? '' : '__other__'
                    }
                    onChange={e => {
                      const v = e.target.value;
                      setBookingForm(f => ({
                        ...f,
                        topic: v === '__other__' ? '' : v,
                      }));
                    }}
                    className={inputCls}
                  >
                    <option value="">Select a course…</option>
                    {courses.map(c => (
                      <option key={c.id} value={c.title}>{c.title}</option>
                    ))}
                    <option value="__other__">Other</option>
                  </select>
                  {/* Show free-text input when Other is chosen (topic is not in courses) */}
                  {bookingForm.topic !== '' &&
                   !courses.some(c => c.title === bookingForm.topic) && (
                    <input
                      value={bookingForm.topic}
                      onChange={e => setBookingForm(f => ({ ...f, topic: e.target.value }))}
                      placeholder="Describe your topic"
                      className={`${inputCls} mt-2`}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#71717A] uppercase tracking-widest mb-2">
                    Message <span className="text-yellow-400">*</span>
                  </label>
                  <textarea
                    value={bookingForm.message}
                    onChange={e => setBookingForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Describe what you want to cover (min 10 characters)…"
                    rows={4}
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#71717A] uppercase tracking-widest mb-2">
                    Mobile Number <span className="text-yellow-400">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={bookingForm.mobileNumber}
                    onChange={e => setBookingForm(f => ({ ...f, mobileNumber: e.target.value }))}
                    placeholder="+965 XXXX XXXX"
                    className={inputCls}
                  />
                </div>

                <div className="h-px bg-white/[0.06]" />

                {createBooking.error && (
                  <p className="text-red-400 text-sm">{createBooking.error.message}</p>
                )}

                <button
                  type="submit"
                  disabled={!bookingForm.mobileNumber || createBooking.isPending}
                  className="w-full py-4 flex items-center justify-center gap-3
                             bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50
                             text-black font-black text-base rounded-3xl
                             transition-all hover:scale-[1.02] disabled:scale-100"
                >
                  {createBooking.isPending ? 'Sending…' : 'Submit booking request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="bg-black border-t border-white/[0.04] py-16 px-6 text-center">
        <img
          src="/logo.png"
          alt="MrPanadol"
          className="logo-bg w-12 h-12 rounded-full object-contain mx-auto mb-4"
        />
        <p className="text-white font-black text-lg mb-6">MrPanadol</p>

        <div className="flex items-center justify-center gap-5 mb-6">
          <a href="https://instagram.com/mrpanadoly" target="_blank" rel="noopener noreferrer"
             className="w-11 h-11 bg-[#18181B] rounded-full flex items-center justify-center
                        text-[#A1A1AA] hover:bg-yellow-400 hover:text-black
                        transition-all hover:scale-110">
            <i className="fab fa-instagram text-lg" />
          </a>
          <a href="https://tiktok.com/@mrpanadooly" target="_blank" rel="noopener noreferrer"
             className="w-11 h-11 bg-[#18181B] rounded-full flex items-center justify-center
                        text-[#A1A1AA] hover:bg-yellow-400 hover:text-black
                        transition-all hover:scale-110">
            <i className="fab fa-tiktok text-lg" />
          </a>
          <a href="https://youtube.com/@mrpanadoly" target="_blank" rel="noopener noreferrer"
             className="w-11 h-11 bg-[#18181B] rounded-full flex items-center justify-center
                        text-[#A1A1AA] hover:bg-yellow-400 hover:text-black
                        transition-all hover:scale-110">
            <i className="fab fa-youtube text-lg" />
          </a>
        </div>

        <p className="text-[#3F3F46] text-xs">© 2026 MrPanadol · Kuwait</p>
      </footer>
    </div>
  );
}
